import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeft,
  Sparkles,
  Trash2,
  Pencil,
  Save,
  X,
  TrendingUp,
  Plus,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MyRules() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [rules, setRules] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [dismissedSuggestionKeys, setDismissedSuggestionKeys] = useState(new Set()); // session-only
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ keyword: "", category: "" });
  const [search, setSearch] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [selectedSuggestions, setSelectedSuggestions] = useState(new Set());
  const [savingBulk, setSavingBulk] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newRule, setNewRule] = useState({ keyword: "", category: "" });
  const [savingNew, setSavingNew] = useState(false);

  const token = () => localStorage.getItem("token");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [rulesRes, catsRes, sugRes] = await Promise.all([
        axios.get(`${API}/tax-helper/user-rules`, {
          headers: { Authorization: `Bearer ${token()}` },
        }),
        axios.get(`${API}/tax-helper/category-buckets/all-categories`, {
          headers: { Authorization: `Bearer ${token()}` },
        }),
        axios.get(`${API}/tax-helper/user-rules/suggestions`, {
          headers: { Authorization: `Bearer ${token()}` },
        }),
      ]);
      setRules(rulesRes.data?.rules || []);
      setCategories(catsRes.data?.categories || []);
      setSuggestions(sugRes.data?.suggestions || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    loadAll();
  }, [user?.id]);

  const categoryLabel = (key) => {
    const c = categories.find((c) => c.key === key);
    return c ? c.label : (key || "—").replace(/_/g, " ");
  };

  const startEdit = (rule) => {
    setEditingId(rule.id);
    setDraft({ keyword: rule.keyword, category: rule.category });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({ keyword: "", category: "" });
  };

  const saveEdit = async (rule) => {
    try {
      const payload = {};
      if (draft.keyword !== rule.keyword) payload.keyword = draft.keyword;
      if (draft.category !== rule.category) payload.category = draft.category;
      if (!Object.keys(payload).length) {
        cancelEdit();
        return;
      }
      const res = await axios.patch(
        `${API}/tax-helper/user-rules/${rule.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setRules((prev) => prev.map((r) => (r.id === rule.id ? res.data.rule : r)));
      toast.success("Rule updated");
      cancelEdit();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const deleteRule = async (ruleId) => {
    try {
      await axios.delete(`${API}/tax-helper/user-rules/${ruleId}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setRules((prev) => prev.filter((r) => r.id !== ruleId));
      toast.success("Rule deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const filtered = rules.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.keyword.toLowerCase().includes(q) ||
      r.category.toLowerCase().includes(q) ||
      (r.source_descriptions || []).some((d) => d.toLowerCase().includes(q))
    );
  });

  const visibleSuggestions = suggestions.filter(s => !dismissedSuggestionKeys.has(s.keyword));

  const toggleSuggestion = (kw) => {
    setSelectedSuggestions(prev => {
      const next = new Set(prev);
      if (next.has(kw)) next.delete(kw); else next.add(kw);
      return next;
    });
  };

  const dismissSuggestion = (kw) => {
    setDismissedSuggestionKeys(prev => new Set(prev).add(kw));
    setSelectedSuggestions(prev => {
      const next = new Set(prev);
      next.delete(kw);
      return next;
    });
  };

  const saveSuggestions = async (keywords) => {
    if (!keywords.length) return;
    setSavingBulk(true);
    const toSave = visibleSuggestions
      .filter(s => keywords.includes(s.keyword))
      .map(s => ({
        keyword: s.keyword,
        category: s.category,
        source_descriptions: s.source_descriptions,
      }));
    try {
      const res = await axios.post(`${API}/tax-helper/user-rules`,
        { rules: toSave },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      toast.success(`Saved ${res.data.saved} rule${res.data.saved === 1 ? '' : 's'}`);
      // Remove saved suggestions from the panel, add them to rules
      setSuggestions(prev => prev.filter(s => !keywords.includes(s.keyword)));
      setSelectedSuggestions(new Set());
      // Refresh rules list
      const rulesRes = await axios.get(`${API}/tax-helper/user-rules`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setRules(rulesRes.data?.rules || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save suggestions");
    } finally {
      setSavingBulk(false);
    }
  };

  const formatCurrency = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const openAddDialog = () => {
    setNewRule({ keyword: "", category: categories[0]?.key || "" });
    setShowAddDialog(true);
  };

  const closeAddDialog = () => {
    setShowAddDialog(false);
    setNewRule({ keyword: "", category: "" });
  };

  const saveNewRule = async () => {
    const keyword = newRule.keyword.trim().toLowerCase();
    const category = newRule.category;
    if (!keyword) {
      toast.error("Keyword is required");
      return;
    }
    if (!category) {
      toast.error("Pick a target category");
      return;
    }
    if (rules.some(r => r.keyword.toLowerCase() === keyword)) {
      toast.error(`A rule with keyword "${keyword}" already exists — edit it instead`);
      return;
    }
    setSavingNew(true);
    try {
      const res = await axios.post(
        `${API}/tax-helper/user-rules`,
        { rules: [{ keyword, category, source_descriptions: [] }] },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      const saved = (res.data?.rules || [])[0];
      if (saved) {
        setRules(prev => [saved, ...prev]);
      } else {
        // fallback refresh
        const rulesRes = await axios.get(`${API}/tax-helper/user-rules`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        setRules(rulesRes.data?.rules || []);
      }
      toast.success(`Rule "${keyword}" → ${categoryLabel(category)} saved`);
      closeAddDialog();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save rule");
    } finally {
      setSavingNew(false);
    }
  };

  const totalApplyCount = rules.reduce((s, r) => s + (r.apply_count || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(-1)}
              className="gap-1.5"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">My Rules</h1>
                <p className="text-xs text-slate-500">
                  Category rules AI Tax Helper has learned from your moves
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Saved Rules</CardDescription>
              <CardTitle className="text-3xl">{rules.length}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Auto-categorizations applied</CardDescription>
              <CardTitle className="text-3xl flex items-center gap-2">
                {totalApplyCount}
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>How it works</CardDescription>
              <CardTitle className="text-sm font-normal text-slate-600 leading-snug">
                When you upload a new statement, AI Tax Helper checks each
                transaction's description against your saved keywords and
                applies the matching category automatically.
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Suggested Rules panel — surfaces patterns from past moves the user hasn't saved yet */}
        {visibleSuggestions.length > 0 && (
          <Card className="mb-6 border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-white" data-testid="suggested-rules-panel">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-indigo-600" />
                    Suggested rules
                    <Badge variant="outline" className="ml-1 bg-indigo-100 text-indigo-700 border-indigo-200">
                      {visibleSuggestions.length}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    Based on transactions you've previously moved across your statements. Save these once — every future statement applies them automatically.
                  </CardDescription>
                </div>
                {selectedSuggestions.size > 0 && (
                  <Button
                    size="sm"
                    onClick={() => saveSuggestions(Array.from(selectedSuggestions))}
                    disabled={savingBulk}
                    data-testid="save-selected-suggestions"
                    className="gap-1.5"
                  >
                    <Save className="w-4 h-4" />
                    Save {selectedSuggestions.size} selected
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {visibleSuggestions.map(s => {
                  const isChecked = selectedSuggestions.has(s.keyword);
                  return (
                    <div
                      key={s.keyword}
                      className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${isChecked ? 'bg-indigo-50 border-indigo-300' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
                      data-testid={`suggestion-${s.keyword}`}
                    >
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleSuggestion(s.keyword)}
                        data-testid={`suggestion-checkbox-${s.keyword}`}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-800">
                          <span className="font-medium">You've moved {s.count}</span>{' '}
                          <span className="font-mono text-indigo-700">"{s.keyword}"</span>{' '}
                          {s.count === 1 ? 'transaction' : 'transactions'} to{' '}
                          <span className="font-medium">{categoryLabel(s.category)}</span>.
                          Save as a rule?
                        </p>
                        <p className="text-xs text-slate-500 truncate mt-0.5" title={(s.source_descriptions || []).join(' • ')}>
                          {(s.source_descriptions || []).slice(0, 3).join(' • ')}
                          {s.source_descriptions.length > 3 && <span className="text-slate-400"> +{s.source_descriptions.length - 3}</span>}
                          <span className="ml-2 text-emerald-600 font-medium">{formatCurrency(s.total_amount)}</span>
                        </p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button
                          size="sm"
                          variant="default"
                          className="h-8"
                          onClick={() => saveSuggestions([s.keyword])}
                          disabled={savingBulk}
                          data-testid={`save-suggestion-${s.keyword}`}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2"
                          onClick={() => dismissSuggestion(s.keyword)}
                          data-testid={`dismiss-suggestion-${s.keyword}`}
                          title="Dismiss for this session"
                        >
                          <X className="w-4 h-4 text-slate-400" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <div className="flex flex-wrap gap-3 items-end justify-between">
              <div>
                <CardTitle>Your category rules</CardTitle>
                <CardDescription>
                  Edit the keyword or target category, or delete a rule you no longer want.
                </CardDescription>
              </div>
              <div className="flex gap-2 items-center w-full sm:w-auto">
                <Input
                  placeholder="Search rules…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full sm:w-64"
                  data-testid="rules-search"
                />
                <Button
                  size="sm"
                  onClick={openAddDialog}
                  className="gap-1.5 shrink-0"
                  data-testid="add-rule-button"
                >
                  <Plus className="w-4 h-4" />
                  Add Rule
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-10 text-slate-500">Loading…</p>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12">
                <Sparkles className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">No rules yet</p>
                <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                  Add a rule manually, or move a transaction to a different category on the
                  Categories tab of any statement and AI Tax Helper will offer to remember the move.
                </p>
                <Button
                  className="mt-4 gap-1.5"
                  onClick={openAddDialog}
                  data-testid="add-rule-empty-state"
                >
                  <Plus className="w-4 h-4" />
                  Add your first rule
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium">Keyword</th>
                      <th className="px-3 py-2 font-medium">Target Category</th>
                      <th className="px-3 py-2 font-medium text-right">Times Fired</th>
                      <th className="px-3 py-2 font-medium">Learned From</th>
                      <th className="px-3 py-2 font-medium w-32">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((rule) => {
                      const editing = editingId === rule.id;
                      return (
                        <tr
                          key={rule.id}
                          className="border-t border-slate-100 hover:bg-slate-50/50"
                          data-testid={`rule-row-${rule.id}`}
                        >
                          <td className="px-3 py-3">
                            {editing ? (
                              <Input
                                value={draft.keyword}
                                onChange={(e) => setDraft({ ...draft, keyword: e.target.value })}
                                className="h-8 text-sm"
                                data-testid={`edit-keyword-${rule.id}`}
                              />
                            ) : (
                              <span className="font-mono text-slate-800">{rule.keyword}</span>
                            )}
                          </td>
                          <td className="px-3 py-3">
                            {editing ? (
                              <select
                                value={draft.category}
                                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                                className="h-8 text-sm rounded border border-slate-300 bg-white px-2"
                                data-testid={`edit-category-${rule.id}`}
                              >
                                {categories.map((c) => (
                                  <option key={c.key} value={c.key}>{c.label}</option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-slate-800">
                                {categoryLabel(rule.category)}
                                {rule.schedule_c_line && (
                                  <span className="ml-2 text-xs text-slate-400">
                                    Line {rule.schedule_c_line}
                                  </span>
                                )}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right tabular-nums">
                            <Badge variant={rule.apply_count > 0 ? "default" : "outline"}>
                              {rule.apply_count || 0}
                            </Badge>
                          </td>
                          <td className="px-3 py-3 max-w-sm">
                            <div className="text-xs text-slate-500 truncate" title={(rule.source_descriptions || []).join(' • ')}>
                              {(rule.source_descriptions || []).slice(0, 2).join(' • ')}
                              {(rule.source_descriptions || []).length > 2 &&
                                <span className="text-slate-400"> +{rule.source_descriptions.length - 2}</span>
                              }
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            {editing ? (
                              <div className="flex gap-1">
                                <Button size="sm" variant="ghost" className="px-2 h-8" onClick={() => saveEdit(rule)} data-testid={`save-${rule.id}`}>
                                  <Save className="w-4 h-4 text-emerald-600" />
                                </Button>
                                <Button size="sm" variant="ghost" className="px-2 h-8" onClick={cancelEdit}>
                                  <X className="w-4 h-4 text-slate-500" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex gap-1">
                                <Button size="sm" variant="ghost" className="px-2 h-8" onClick={() => startEdit(rule)} data-testid={`edit-${rule.id}`}>
                                  <Pencil className="w-4 h-4 text-slate-500" />
                                </Button>
                                <Button size="sm" variant="ghost" className="px-2 h-8" onClick={() => setConfirmDeleteId(rule.id)} data-testid={`delete-${rule.id}`}>
                                  <Trash2 className="w-4 h-4 text-rose-500" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Delete this rule?</CardTitle>
              <CardDescription>
                Future statements won't auto-categorize matching transactions any more.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
              <Button variant="destructive" onClick={() => deleteRule(confirmDeleteId)} data-testid="confirm-delete-rule">
                Delete
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {showAddDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md" data-testid="add-rule-dialog">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-indigo-600" />
                Add a custom rule
              </CardTitle>
              <CardDescription>
                When any transaction's description contains your keyword (case-insensitive),
                it will be auto-categorized to the chosen category on every future statement.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">
                  Keyword <span className="text-rose-500">*</span>
                </label>
                <Input
                  value={newRule.keyword}
                  onChange={(e) => setNewRule({ ...newRule, keyword: e.target.value })}
                  placeholder="e.g. starbucks"
                  autoFocus
                  data-testid="new-rule-keyword"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Tip: keep it short and unique — "starbucks" matches every variant of the description.
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">
                  Target category <span className="text-rose-500">*</span>
                </label>
                <select
                  value={newRule.category}
                  onChange={(e) => setNewRule({ ...newRule, category: e.target.value })}
                  className="w-full h-9 text-sm rounded border border-slate-300 bg-white px-2"
                  data-testid="new-rule-category"
                >
                  <option value="">Pick a category…</option>
                  {categories.map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={closeAddDialog} disabled={savingNew}>
                  Cancel
                </Button>
                <Button onClick={saveNewRule} disabled={savingNew} data-testid="confirm-add-rule">
                  {savingNew ? "Saving…" : "Save rule"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
